import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Save } from "lucide-react";

interface SiteSetting {
  id: string;
  key: string;
  value: string | null;
  description: string | null;
  is_public: boolean;
}

export function SiteSettingsManager() {
  const [settings, setSettings] = useState<SiteSetting[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('*')
        .order('key');

      if (error) throw error;
      setSettings(data || []);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar configurações.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateSetting = async (key: string, value: string) => {
    try {
      const { error } = await supabase
        .from('site_settings')
        .update({ 
          value,
          updated_at: new Date().toISOString()
        })
        .eq('key', key);

      if (error) throw error;

      setSettings(settings.map(setting => 
        setting.key === key ? { ...setting, value } : setting
      ));

      toast({
        title: "Sucesso!",
        description: "Configuração atualizada com sucesso.",
      });
    } catch (error) {
      console.error('Error updating setting:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar configuração.",
        variant: "destructive",
      });
    }
  };

  const handleSaveAll = async () => {
    setLoading(true);
    try {
      for (const setting of settings) {
        await supabase
          .from('site_settings')
          .update({ 
            value: setting.value,
            updated_at: new Date().toISOString()
          })
          .eq('key', setting.key);
      }

      toast({
        title: "Sucesso!",
        description: "Todas as configurações foram salvas.",
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Erro",
        description: "Erro ao salvar configurações.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const updateLocalSetting = (key: string, value: string) => {
    setSettings(settings.map(setting => 
      setting.key === key ? { ...setting, value } : setting
    ));
  };

  const renderSettingInput = (setting: SiteSetting) => {
    // Boolean settings (toggle)
    if (setting.key === 'show_comparison_tables') {
      return (
        <div className="flex items-center gap-2">
          <Switch
            checked={setting.value === 'true'}
            onCheckedChange={(checked) => {
              const newValue = checked ? 'true' : 'false';
              updateLocalSetting(setting.key, newValue);
              handleUpdateSetting(setting.key, newValue);
            }}
          />
          <Label className="cursor-pointer">
            {setting.value === 'true' ? 'Ativado' : 'Desativado'}
          </Label>
        </div>
      );
    }

    // Text settings (input)
    return (
      <div className="flex gap-2">
        <Input
          value={setting.value || ''}
          onChange={(e) => updateLocalSetting(setting.key, e.target.value)}
          placeholder={`Digite ${setting.key.replace(/_/g, ' ')}`}
        />
        <Button
          onClick={() => handleUpdateSetting(setting.key, setting.value || '')}
          variant="outline"
          size="sm"
        >
          Salvar
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Configurações do Site</h3>
        <Button onClick={handleSaveAll} disabled={loading}>
          <Save className="w-4 h-4 mr-2" />
          Salvar Tudo
        </Button>
      </div>

      <div className="grid gap-4">
        {settings.map((setting) => (
          <Card key={setting.id}>
            <CardHeader className="pb-3">
              <CardTitle className="text-base capitalize">
                {setting.key.replace(/_/g, ' ')}
              </CardTitle>
              {setting.description && (
                <p className="text-sm text-gray-600">{setting.description}</p>
              )}
            </CardHeader>
            <CardContent className="pt-0">
              <div className="mb-3">
                {renderSettingInput(setting)}
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className={setting.is_public ? "text-green-600" : "text-orange-600"}>
                  {setting.is_public ? "🌐 Público" : "🔒 Privado"}
                </span>
                <span className="text-gray-500">
                  {setting.is_public ? "(visível para todos)" : "(apenas admins)"}
                </span>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
